import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/apiClient';
import { queryKeys } from '@/queries/keys';
import type { AuthUser } from '@/lib/types';

// Full-page navigation target that kicks off Microsoft Entra SSO. The backend
// redirects the browser to Microsoft and, after the callback, back to
// /auth/callback?token=… (or ?error=…).
export const MICROSOFT_LOGIN_URL = '/api/auth/microsoft/start';
// Full-page navigation that ends the Microsoft Entra session and returns to the SPA.
export const MICROSOFT_LOGOUT_URL = '/api/auth/microsoft/logout';

export const authApi = {
  me: () => call<AuthUser>('/auth/me'),
  // Local-only convenience sign-in (disabled in production).
  devLogin: (email: string) =>
    call<{ user: AuthUser; token: string }>('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  // Email OTP sign-in: request a code, then verify it for a user + token.
  requestOtp: (email: string) =>
    call<{ status: string }>('/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyOtp: (email: string, code: string) =>
    call<{ user: AuthUser; token: string }>('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),
  logout: () => call<{ status: string }>('/auth/logout', { method: 'POST' }),
};

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: authApi.me,
    enabled,
    retry: false,
  });
}

export function useDevLogin() {
  return useMutation({ mutationFn: (email: string) => authApi.devLogin(email) });
}

export function useRequestOtp() {
  return useMutation({ mutationFn: (email: string) => authApi.requestOtp(email) });
}

export function useVerifyOtp() {
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      authApi.verifyOtp(email, code),
  });
}

export function useLogout() {
  return useMutation({ mutationFn: authApi.logout });
}
